import React, { useEffect, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { getCoachByPlayerProfile } from '../../../../services/firestoreService';
import { getLatestCoachUpdate } from '../../../../services/coachUpdatesService';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'left',
  defaultPadding: 'normal',
};

/**
 * Parse **bold** markers in a string.
 */
const parseBold = (text) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const CoachUpdatesRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const [update, setUpdate] = useState(null);
  const [coachName, setCoachName] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  useEffect(() => {
    if (!profileId) {
      setIsLoading(false);
      return;
    }

    const load = async () => {
      try {
        const coach = await getCoachByPlayerProfile(profileId);
        if (coach?.id) {
          setCoachName(coach.displayName || 'Coach');
          const latest = await getLatestCoachUpdate(coach.id);
          setUpdate(latest);
        }
      } catch (err) {
        console.error('Error loading coach update:', err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [profileId]);

  if (isLoading) return null;

  const heading = update?.heading || content.fallbackHeading || 'Latest Update';
  const body = update?.body || content.fallbackBody || 'No updates posted yet.';
  const lines = body.split('\n');

  // Format date if we have a real update
  let dateStr = null;
  if (update?.createdAt) {
    const date = update.createdAt?.toDate?.() || new Date(update.createdAt);
    dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-2xl mx-auto">
        <div
          className="relative rounded-xl border p-5 sm:p-6 overflow-hidden"
          style={{
            backgroundColor: styles.cardBackground,
            borderColor: styles.borderColor,
          }}
        >
          {/* Decorative icon */}
          <ClipboardList
            size={48}
            className="absolute top-4 right-4 pointer-events-none"
            style={{ color: styles.bodyColor, opacity: 0.08 }}
          />

          {/* Heading */}
          {heading && (
            <h3
              className={`${styles.headingSizeClass} font-bold mb-2`}
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h3>
          )}

          {/* Author + Date */}
          {(coachName || dateStr) && (
            <p
              className="text-sm mb-4"
              style={{ color: styles.bodyColor, opacity: 0.6 }}
            >
              {coachName && <span style={{ fontWeight: 600, opacity: 1 }}>{coachName}</span>}
              {coachName && dateStr && <span> &middot; </span>}
              {dateStr && <span>{dateStr}</span>}
            </p>
          )}

          {/* Body lines */}
          <div className="space-y-1 relative">
            {lines.map((line, i) =>
              line.trim() === '' ? (
                <div key={i} className="h-2" />
              ) : (
                <p
                  key={i}
                  className="text-base sm:text-lg leading-relaxed"
                  style={{ color: styles.bodyColor, fontFamily: styles.bodyFontStyle }}
                >
                  {parseBold(line)}
                </p>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachUpdatesRenderer;
